import {
  type CareThread,
  type ClassMood,
  type LessonItem,
  MOODS,
  type SchoolEvent,
  type StudentGrade,
  type ViewContext,
  addDays,
  canJoinLesson,
  canOpenClassBoard,
  gradeLevel,
  isLessonLive,
  petStage,
  pointsAverage,
  shortDate,
  shortName,
  termFor,
  timeOf,
  todayIso,
  useAbsencesOn,
  useBirthdays,
  useCareThreads,
  useClassPet,
  useClasses,
  useEvents,
  useLessonPresence,
  useMemberName,
  useMenu,
  useMoodOverview,
  useStartDemoClassLesson,
  useStartDemoLesson,
  useStudentRecord,
  useTerms,
  useUpcomingDay,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { ChalkboardTeacherIcon } from '@phosphor-icons/react/ChalkboardTeacher';
import { ShieldIcon } from '@phosphor-icons/react/Shield';
import { Link, useNavigate } from '@tanstack/react-router';
import { type ReactNode, type RefObject, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useCurrentContext } from '../app/ViewContext';
import { useNow } from '../components/AlarmBanner';
import { ClassesNow } from '../components/ClassesNow';
import { threadTitle, useWhoLabel } from '../care/shared';
import {
  Block,
  Chip,
  DARK,
  EventArt,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  OpenArrow,
  PAPER,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  TaskArt,
  grad,
  pillClass,
  ringButton,
  ringSmall,
} from '../design/forms';
import {
  ArrowUpRightIcon,
  CakeIcon,
  ChatCircleIcon,
  type FilledIcon,
  ForkKnifeIcon,
  SirenIcon,
  VideoCameraIcon,
} from '../design/icons';
import { readEnv } from '../env';
import { gradeShort } from '../lib/labels';
import { useMinute } from '../lib/now';
import { useEventWhen } from './EventsPage';
import { PendingFormsCard } from './FormsPage';
import { AbroadCard } from '../student/AbroadPage';
import { CatchUp } from '../student/CatchUp';
import { StudentHome } from '../student/StudentHome';
import { MoodArt } from '../student/home/LineArt';
import {
  capitalize,
  dayLong,
  dayMonthShort,
  plain,
  roomOf,
  weekdayKey,
} from '../student/home/model';
import { FLAT, Shape } from '../student/home/ShapesArt';
import { EVENT_GRAD } from '../student/home/ShapesDay';

/*
 * The homes of the grown-ups in «Форми 2» (28.10, his «распространять по всей системе»), in the language
 * of the student's home: a greeting free on the paper — «вітаємо,» by hand (the one handwritten line), the
 * name big and light, the date under the orange star, the day in a short sentence with its counts as
 * marks; beside it a lime card with what matters to that person now (the child's term, my class, the
 * school today, the threads waiting). Then white blocks: the lessons as soft rows in their subject's
 * gradient with its picture big and quiet behind, the grades as small tiles, the school's life as cards of
 * its events. What each role saw stays; only the look and the order change.
 */

type Day = ReturnType<typeof useUpcomingDay>;
type Family = Extract<ViewContext, { kind: 'guardian' }>;
type Perspective = 'student' | 'teacher' | 'school';

/** The top of a sticky column: under the bar, or higher for a column taller than the window. */
function useStickyTop(ref: RefObject<HTMLDivElement | null>) {
  const [top, setTop] = useState(80);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const fit = () => setTop(Math.min(80, window.innerHeight - el.offsetHeight - 16));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    window.addEventListener('resize', fit);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [ref]);
  return top;
}

/** The person's first name, or the role while it loads. */
function useFirstName(memberId: string, fallback: string) {
  const name = useMemberName(memberId).data;
  return name?.split(' ')[0] || fallback;
}

// —— small parts ——

/** A count set into the words: a soft rectangle in a colour — a mark, not a button. */
function Mark({ tone, children }: { tone: Grad; children: ReactNode }) {
  return (
    <span
      className="rounded-[10px] px-2 py-0.5 whitespace-nowrap"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      {children}
    </span>
  );
}

/** A status beside a row's words: a small soft rectangle in its colour. */
function Label({ tone, children }: { tone: Grad; children: ReactNode }) {
  return (
    <span
      className="inline-flex h-6 shrink-0 items-center rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      {children}
    </span>
  );
}

/** ↗ into a section: an outlined circle. */
function Ring({
  to,
  label,
}: {
  to: '/journal' | '/care' | '/grades' | '/schedule' | '/canteen' | '/events';
  label: string;
}) {
  return (
    <Link to={to} aria-label={label} title={label} className={ringButton}>
      <ArrowUpRightIcon size={16} weight="bold" aria-hidden />
    </Link>
  );
}

/** A filled icon in a gradient circle — for what has no picture of its own. */
function Dot({ icon: Icon, tone, size = 40 }: { icon: FilledIcon; tone: Grad; size?: number }) {
  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-full"
      style={{ width: size, height: size, backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      <Icon size={Math.round(size * 0.45)} weight="fill" />
    </span>
  );
}

/** Words for an empty list, quiet on the paper. */
function Quiet({ children }: { children: ReactNode }) {
  return (
    <p
      className="rounded-[20px] px-4 py-5 text-[14px]"
      style={{ backgroundColor: PAPER, color: INK_SOFT }}
    >
      {children}
    </p>
  );
}

/** A big light number with its words under it (on the lime card). */
function Count({ value, label, big = false }: { value: ReactNode; label: string; big?: boolean }) {
  return (
    <div className="min-w-0">
      <p
        className={`font-display font-light tracking-[-0.04em] tabular-nums ${big ? 'text-[52px] leading-[56px]' : 'text-[30px] leading-9'}`}
        style={{ color: INK }}
      >
        {value}
      </p>
      <p className="truncate text-[13px] leading-[18px]">{label}</p>
    </div>
  );
}

// —— the greeting ——

/** «вітаємо,» by hand, the name big and light, the date under the orange star; the day line under. */
function Greeting({ name, children }: { name: string; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="relative flex min-w-0 flex-col gap-5">
      <div className="min-w-0">
        <p
          className="-mb-1 -ml-0.5 inline-block -rotate-3 font-hand text-[32px] leading-9 font-bold md:text-[38px] md:leading-10"
          style={{ color: INK_SOFT }}
        >
          {t('home.hi')}
        </p>
        <h1
          className="truncate font-display text-[46px] leading-[52px] font-light tracking-[-0.045em] md:text-[64px] md:leading-[70px]"
          style={{ color: INK }}
        >
          {name}
        </h1>
        <p
          className="mt-1.5 flex items-center gap-2 text-[14px] font-medium"
          style={{ color: INK_SOFT }}
        >
          <Shape kind="star" size={13} color={FLAT.orange} />
          {dayLong(todayIso())}
        </p>
      </div>
      {children}
    </div>
  );
}

/** The greeting and, beside it, the lime card of the moment. */
function Hero({ left, right }: { left: ReactNode; right?: ReactNode }) {
  return (
    <section
      className={`grid min-w-0 grid-cols-1 items-center gap-6 pt-2 pb-2 xl:col-span-12 ${right ? 'lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)] lg:gap-12' : ''}`}
    >
      {left}
      {right}
    </section>
  );
}

/** The lime card: a small label on top with its ↗, then what matters now. */
function LimeCard({
  label,
  action,
  art,
  children,
}: {
  label: string;
  action?: ReactNode;
  art?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className="relative isolate flex min-w-0 flex-col overflow-hidden rounded-[28px] p-5 md:p-6"
      style={{ backgroundImage: grad('lime'), color: GRAD.lime.ink }}
    >
      {art}
      <div className="flex min-h-9 items-center gap-2 text-[13px] font-medium">
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {action}
      </div>
      {children}
    </div>
  );
}

/** «Сьогодні», «Завтра», «У понеділок» — the day the lessons shown are on. */
function whenOf(t: TFunction, day: Day) {
  if (day.isToday || !day.date) return t('studentHome.shapes.today');
  if (day.date === addDays(todayIso(), 1)) return t('studentHome.shapes.tomorrow');
  return capitalize(t(`student.on.${weekdayKey(day.date)}`));
}

/** The day in one sentence, its counts marked: «Кирило сьогодні — 7 уроків, 2 контрольні.» */
function DayLine({ day, who }: { day: Day; who?: string }) {
  const { t } = useTranslation();
  if (day.isPending) return <p className="h-9" aria-busy />;
  const held = day.lessons.filter((l) => l.status !== 'cancelled');
  if (!day.date || held.length === 0)
    return (
      <p className="text-[22px] leading-9 font-light" style={{ color: INK_SOFT }}>
        {t('home.noLessonsSoon')}
      </p>
    );
  const tests = held.filter((l) => l.isTest).length;
  const when = whenOf(t, day);
  return (
    <p
      className="max-w-[620px] font-display text-[21px] leading-[38px] font-light tracking-[-0.01em] md:text-[24px] md:leading-[42px]"
      style={{ color: INK_SOFT }}
    >
      <span style={{ color: INK }}>{who ? `${who} ${when.toLowerCase()}` : when}</span> —{' '}
      <Mark tone="lavender">{t('student.lessonsCount', { count: held.length })}</Mark>
      {tests > 0 && (
        <>
          , <Mark tone="coral">{t('studentHome.shapes.tests', { count: tests })}</Mark>
        </>
      )}
      .
    </p>
  );
}

// —— the lessons: soft rows in the subject's gradient, its picture behind ——

const smallDark =
  'inline-flex h-9 shrink-0 items-center rounded-full px-4 text-[13px] font-medium whitespace-nowrap text-white transition-colors hover:bg-[#2f2357]';

function LessonRow({
  lesson,
  perspective,
  memberId,
  family,
  now,
}: {
  lesson: LessonItem;
  perspective: Perspective;
  memberId?: string;
  /** families follow the lesson from home: no video room, no board */
  family: boolean;
  now: number;
}) {
  const { t } = useTranslation();
  const look = subjectLook(lesson.subject);
  const cancelled = lesson.status === 'cancelled';
  const tone: Grad = cancelled ? 'stone' : SUBJECT_GRAD[look.tone];
  const today = todayIso();
  const done = lesson.date === today && Date.parse(lesson.endsAt) < now;
  const teacher = lesson.substitute ?? lesson.teachers[0];
  const meta = [
    t('schedule.period', { n: lesson.period }),
    perspective !== 'student' ? lesson.classLabel : null,
    perspective !== 'teacher' && teacher ? shortName(teacher) : null,
    lesson.groupLabel,
    lesson.room ? roomOf(t, lesson.room) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const join = !family && canJoinLesson(lesson, today);
  const board = !family && canOpenClassBoard(lesson, today);
  return (
    <div
      className={`relative isolate flex min-h-[60px] flex-wrap items-center gap-x-3 gap-y-2 overflow-hidden rounded-[20px] py-2.5 pr-4 pl-4 ${done || cancelled ? 'opacity-55' : ''}`}
      style={{ backgroundImage: grad(tone) }}
    >
      <SubjectArt
        icon={look.icon}
        tone={tone}
        size={86}
        ghost
        className="pointer-events-none absolute top-1/2 right-[68px] -z-10 -translate-y-[42%]"
      />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
          <span
            className={`truncate text-[16px] leading-[22px] font-medium ${cancelled ? 'line-through' : ''}`}
            style={{ color: cancelled ? INK : GRAD[tone].ink }}
          >
            {lesson.subject}
          </span>
          {cancelled ? (
            <Chip soft>{t('schedule.cancelled')}</Chip>
          ) : (
            <>
              {lesson.isTest && (
                <Chip soft tone="orange">
                  {t('schedule.test')}
                </Chip>
              )}
              {lesson.format === 'online' && <Chip soft>{t('format.online')}</Chip>}
              {lesson.format === 'shelter' && <Chip soft>{t('format.shelter')}</Chip>}
              {lesson.substitute && (
                <Chip soft>
                  {lesson.substitute.id === memberId
                    ? t('schedule.substituting')
                    : t('schedule.substitute', { name: shortName(lesson.substitute) })}
                </Chip>
              )}
            </>
          )}
        </span>
        <span className="block truncate text-[12.5px] leading-[18px]" style={{ color: INK_SOFT }}>
          {meta}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end leading-tight" style={{ color: INK }}>
        <span className="font-display text-[17px] font-light tracking-[-0.02em]">
          {timeOf(lesson.startsAt)}
        </span>
        <span className="text-[12px]" style={{ color: INK_SOFT }}>
          {timeOf(lesson.endsAt)}
        </span>
      </span>
      {(join || board) && (
        <span className="flex w-full flex-wrap gap-1.5">
          {join && (
            <Link
              to="/lesson/$lessonId"
              params={{ lessonId: lesson.id }}
              className={smallDark}
              style={{ backgroundColor: DARK }}
            >
              {perspective === 'teacher' ? t('lesson.start') : t('lesson.join')}
            </Link>
          )}
          {board && (
            <Link
              to="/class-board/$lessonId"
              params={{ lessonId: lesson.id }}
              className={pillClass('light', 'sm').replace('pr-1', 'pr-3.5')}
            >
              {perspective === 'teacher' ? plain(t('classBoard.lead')) : t('classBoard.open')}
            </Link>
          )}
        </span>
      )}
    </div>
  );
}

/** The day's title: «Сьогодні», «Завтра», «У понеділок» with its date. */
function useDayTitle(day: Day) {
  const { t } = useTranslation();
  if (day.isToday || !day.date) return { title: t('home.today') };
  return { title: whenOf(t, day), note: dayMonthShort(day.date) };
}

function Lessons({
  day,
  perspective,
  memberId,
  family = false,
}: {
  day: Day;
  perspective: Perspective;
  memberId?: string;
  family?: boolean;
}) {
  const { t } = useTranslation();
  const now = useMinute();
  const { title, note } = useDayTitle(day);
  return (
    <Block>
      <Head title={title} {...(note && { note })}>
        <OpenArrow to="/schedule" label={t('home.fullSchedule')} soft />
      </Head>
      {day.isPending ? (
        <div
          className="h-[240px] animate-pulse rounded-[20px]"
          style={{ backgroundColor: PAPER }}
          aria-busy
        />
      ) : day.lessons.length === 0 ? (
        <Quiet>{t('home.noLessonsSoon')}</Quiet>
      ) : (
        <div className="flex flex-col gap-2">
          {day.lessons.map((l) => (
            <LessonRow
              key={l.id}
              lesson={l}
              perspective={perspective}
              {...(memberId !== undefined && { memberId })}
              family={family}
              now={now}
            />
          ))}
        </div>
      )}
    </Block>
  );
}

// —— the school's life: its events as cards with their picture, the lunch and the birthdays ——

function EventCard({ event, when, stack }: { event: SchoolEvent; when: string; stack: boolean }) {
  const tone = EVENT_GRAD[event.kind];
  return (
    <Link
      to="/events"
      className={`relative isolate flex min-w-0 snap-start flex-col overflow-hidden rounded-[24px] p-4 transition-transform duration-300 ease-spring hover:-translate-y-0.5 sm:grow ${stack ? 'min-h-[132px] sm:basis-full' : 'min-h-[156px] sm:basis-[170px]'}`}
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      <EventArt
        kind={event.kind}
        tone={tone}
        size={136}
        ghost
        className="pointer-events-none absolute -top-4 -right-5 -z-10"
      />
      <span
        className="self-start rounded-[7px] bg-white/75 px-2 py-1 text-[12px] leading-none font-medium whitespace-nowrap"
        style={{ color: INK }}
      >
        {when}
      </span>
      <span className="mt-auto pt-4 font-display text-[20px] leading-[25px] font-light tracking-[-0.02em]">
        {event.title}
      </span>
      {event.place && <span className="mt-1.5 truncate text-[12.5px]">{event.place}</span>}
    </Link>
  );
}

/** A row of the school's life: its icon in a circle, the words, ↗ when it leads somewhere. */
function LifeRow({
  icon,
  tone,
  title,
  text,
  to,
}: {
  icon: FilledIcon;
  tone: Grad;
  title: string;
  text: ReactNode;
  to?: '/canteen';
}) {
  const body = (
    <>
      <Dot icon={icon} tone={tone} />
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] leading-5 font-medium" style={{ color: INK }}>
          {title}
        </span>
        <span className="block text-[13px] leading-[18px]" style={{ color: INK_SOFT }}>
          {text}
        </span>
      </span>
    </>
  );
  return (
    <li style={{ borderTop: `1px solid ${LINE}` }}>
      {to ? (
        <Link to={to} className="group flex items-center gap-3.5 py-3">
          {body}
          <span className={`${ringSmall} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}>
            <ArrowUpRightIcon size={14} weight="bold" aria-hidden />
          </span>
        </Link>
      ) : (
        <div className="flex items-center gap-3.5 py-3">{body}</div>
      )}
    </li>
  );
}

/**
 * «Життя школи»: the next events, today's lunch, the birthdays of the week in my classes. The events
 * side by side (on a phone — a row to swipe); in a narrow column (`stack`) one under another.
 */
function SchoolLife({ schoolId, stack = false }: { schoolId: string; stack?: boolean }) {
  const { t } = useTranslation();
  const today = todayIso();
  const menu = useMenu(schoolId, today, today);
  const events = useEvents(schoolId, today);
  const birthdays = useBirthdays(schoolId, 7);
  const classes = useClasses(schoolId);
  const when = useEventWhen();
  const lunch = (menu.data ?? []).filter((i) => i.meal === 'lunch');
  const next = (events.data ?? []).slice(0, 3);
  const born = birthdays.data ?? [];
  if (lunch.length === 0 && next.length === 0 && born.length === 0) return null;
  const classLabel = (id: string) => classes.data?.find((c) => c.id === id)?.label ?? '';
  const bornWhen = (iso: string) =>
    iso === today
      ? plain(t('birthdays.today'))
      : iso === addDays(today, 1)
        ? t('birthdays.tomorrow')
        : shortDate(iso);
  return (
    <Block>
      <Head title={t('nav.schoolLife')}>
        <OpenArrow to="/events" label={t('events.upcoming')} soft />
      </Head>
      {next.length > 0 && (
        <div
          className={`no-scrollbar grid snap-x snap-mandatory grid-flow-col gap-2.5 overflow-x-auto sm:flex sm:flex-wrap sm:overflow-visible ${next.length === 1 ? 'auto-cols-[100%]' : 'auto-cols-[78%]'}`}
        >
          {next.map((e) => (
            <EventCard key={e.id} event={e} when={when(e)} stack={stack} />
          ))}
        </div>
      )}
      {(lunch.length > 0 || born.length > 0) && (
        <ul className={`flex flex-col ${next.length > 0 ? 'mt-4' : ''}`}>
          {lunch.length > 0 && (
            <LifeRow
              icon={ForkKnifeIcon}
              tone="apricot"
              title={t('canteen.today')}
              text={lunch.map((i) => i.title).join(' · ')}
              to="/canteen"
            />
          )}
          {born.length > 0 && (
            <LifeRow
              icon={CakeIcon}
              tone="pink"
              title={t('birthdays.title')}
              text={born.map((b) => (
                <span key={b.memberId} className="mr-3 inline-block">
                  <span style={{ color: INK }}>{b.name}</span>{' '}
                  <span style={{ color: INK_FAINT }}>{classLabel(b.classId)}</span> —{' '}
                  {bornWhen(b.onDate)}
                </span>
              ))}
            />
          )}
        </ul>
      )}
    </Block>
  );
}

// —— the family ——

/** «Зараз онлайн-урок» on the child's card: whether the child is in the video room (from LiveKit). */
function LiveRow({ ctx, day }: { ctx: Family; day: Day }) {
  const { t } = useTranslation();
  const now = useNow(10_000);
  const lesson = day.lessons.find((l) => isLessonLive(l, now));
  const presence = useLessonPresence(lesson?.id, true);
  if (!lesson) return null;
  const child = presence.data?.get(ctx.child.memberId);
  const name = ctx.child.firstName;
  return (
    <div className="mt-5 flex items-center gap-3 rounded-[20px] bg-white p-3 pl-4">
      <span className="relative flex size-2.5 shrink-0">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-[#FF6A3D] opacity-60" />
        <span className="relative inline-flex size-2.5 rounded-full bg-[#FF6A3D]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] leading-5 font-medium" style={{ color: INK }}>
          {t('lesson.live.now')}: {lesson.subject}
        </span>
        <span
          className="block truncate text-[12.5px] leading-[18px]"
          style={{ color: child?.online ? GRAD.mint.ink : INK_SOFT }}
        >
          {child?.online
            ? t('lesson.live.inRoom', { name, time: timeOf(child.firstJoinedAt) })
            : child?.lastLeftAt
              ? t('lesson.live.left', { name, time: timeOf(child.lastLeftAt) })
              : t('lesson.live.notYet', { name })}
        </span>
      </span>
      <span className="shrink-0 text-[12.5px]" style={{ color: INK_SOFT }}>
        {t('lesson.live.until', { time: timeOf(lesson.endsAt) })}
      </span>
    </div>
  );
}

const LEVEL_GRAD: ReadonlyArray<Grad> = ['coral', 'butter', 'sky', 'lime'];

/**
 * The new grades as small tiles side by side (as on the student's home): the grade in the interface's font, the
 * subject's picture, the subject, what for and the day, on the level's gradient.
 */
function Grades({ grades, pending }: { grades: ReadonlyArray<StudentGrade>; pending: boolean }) {
  const { t } = useTranslation();
  const places = 8;
  const shown = grades.length > places ? grades.slice(0, places - 1) : grades;
  const more = grades.length - shown.length;
  const tile =
    'group flex h-full min-h-[128px] min-w-0 flex-col rounded-[22px] p-3.5 transition-transform duration-300 ease-spring hover:-translate-y-0.5';
  return (
    <Block>
      <Head title={t('home.latestGrades')}>
        <OpenArrow to="/grades" label={t('home.allGrades')} soft />
      </Head>
      {pending ? (
        <div
          className="h-[128px] animate-pulse rounded-[22px]"
          style={{ backgroundColor: PAPER }}
          aria-busy
        />
      ) : grades.length === 0 ? (
        <Quiet>{t('grades.noGrades')}</Quiet>
      ) : (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(128px,1fr))] gap-2">
          {shown.map((g) => {
            const thematic = g.kind === 'thematic' || g.kind === 'semester' || g.kind === 'year';
            const tone: Grad = thematic
              ? 'lavender'
              : g.value
                ? (LEVEL_GRAD[gradeLevel(g.value)] ?? 'stone')
                : 'stone';
            return (
              <li key={g.id} className="min-w-0">
                <Link
                  to="/grades"
                  className={tile}
                  title={g.comment ?? undefined}
                  style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
                >
                  <span className="flex items-start justify-between gap-1">
                    <span
                      className="-mt-1 font-display text-[38px] leading-[44px] font-light tracking-[-0.03em] tabular-nums"
                      style={{ color: INK }}
                    >
                      {gradeShort(t, g.value, g.special)}
                    </span>
                    <SubjectArt icon={subjectLook(g.subject).icon} tone={tone} size={32} />
                  </span>
                  <span className="mt-auto line-clamp-2 pt-2 text-[14px] leading-[18px] font-medium">
                    {g.subject}
                  </span>
                  <span
                    className="mt-0.5 flex flex-col text-[11.5px] leading-4"
                    style={{ color: INK_SOFT }}
                  >
                    <span className="truncate">{g.topic ?? t(`journal.kinds.${g.kind}`)}</span>
                    <span>{dayMonthShort(g.date)}</span>
                  </span>
                </Link>
              </li>
            );
          })}
          {more > 0 && (
            <li className="min-w-0">
              <Link to="/grades" className={tile} style={{ backgroundColor: PAPER, color: INK }}>
                <span className="flex items-start justify-between gap-1">
                  <span className="font-display text-[30px] leading-[40px] font-light tracking-[-0.03em]">
                    +{more}
                  </span>
                  <span
                    className={`${ringSmall} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}
                  >
                    <ArrowUpRightIcon size={14} weight="bold" aria-hidden />
                  </span>
                </span>
                <span className="mt-auto pt-2 text-[14px] leading-[18px] font-medium">
                  {t('home.allGrades')}
                </span>
              </Link>
            </li>
          )}
        </ul>
      )}
    </Block>
  );
}

function FamilyHome({ ctx }: { ctx: Family }) {
  const { t } = useTranslation();
  const name = useFirstName(ctx.memberId, t('roles.guardian'));
  const day = useUpcomingDay(ctx.schoolId, { kind: 'student', studentId: ctx.child.memberId });
  const terms = useTerms(ctx.schoolId);
  const term = terms.data && termFor(terms.data, todayIso());
  const record = useStudentRecord(ctx.child.memberId, term?.id);
  const grades = record.data?.grades ?? [];
  const average = pointsAverage(grades);
  const column = useRef<HTMLDivElement>(null);
  const top = useStickyTop(column);
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <Hero
        left={
          <Greeting name={name}>
            <DayLine day={day} who={ctx.child.firstName} />
          </Greeting>
        }
        right={
          <LimeCard
            label={`${ctx.child.firstName} ${ctx.child.lastName}${ctx.child.class ? `, ${ctx.child.class.label}` : ''}`}
            action={<Ring to="/grades" label={t('home.allGrades')} />}
          >
            <div className="mt-3 grid grid-cols-2 gap-4">
              <Count big value={average ?? '—'} label={t('home.average')} />
              <Count
                big
                value={record.data?.absences.length ?? '—'}
                label={t('home.absencesTerm')}
              />
            </div>
            <LiveRow ctx={ctx} day={day} />
          </LimeCard>
        }
      />
      <div ref={column} className="min-w-0 xl:sticky xl:col-span-5 xl:self-start" style={{ top }}>
        <Lessons day={day} perspective="student" memberId={ctx.memberId} family />
      </div>
      {/* beside the lessons: a plain stack — a block of the moment comes or goes, the others close up */}
      <div className="flex min-w-0 flex-col gap-4 xl:col-span-7">
        <AbroadCard studentId={ctx.child.memberId} self={false} />
        <PendingFormsCard />
        <CatchUp schoolId={ctx.schoolId} studentId={ctx.child.memberId} canTick={false} />
        <Grades grades={grades.slice(0, 12)} pending={record.isPending && Boolean(term)} />
        <SchoolLife schoolId={ctx.schoolId} />
      </div>
    </div>
  );
}

// —— the teacher ——

const demoStack = () =>
  readEnv(import.meta.env as Record<string, string | undefined>).appEnv !== 'production';

/** Demo stacks only: an online lesson with the demo class right now, on any day. */
function DemoLessonButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const start = useStartDemoLesson();
  if (!demoStack()) return null;
  return (
    <button
      type="button"
      disabled={start.isPending}
      className={`${pillClass('light', 'sm')} disabled:opacity-50`}
      onClick={() =>
        void start
          .mutateAsync()
          .then((lessonId) => navigate({ to: '/lesson/$lessonId', params: { lessonId } }))
      }
    >
      {t('lesson.demoNow')}
      <PillIcon icon={VideoCameraIcon} size="sm" />
    </button>
  );
}

/** Demo stacks only: an ordinary lesson in the classroom right now — for «Дошка в класі». */
function DemoClassLessonButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const start = useStartDemoClassLesson();
  if (!demoStack()) return null;
  return (
    <button
      type="button"
      disabled={start.isPending}
      className={`${pillClass('light', 'sm')} disabled:opacity-50`}
      onClick={() =>
        void start
          .mutateAsync()
          .then((lessonId) =>
            navigate({ to: '/class-board/$lessonId', params: { lessonId }, search: {} }),
          )
      }
    >
      {t('classBoard.demoNow')}
      <PillIcon icon={ChalkboardTeacherIcon} size="sm" />
    </button>
  );
}

/**
 * «Мій клас» on lime: the class's dragon — its stage, how far to the next, this week's care (no names),
 * the journal one ↗ away.
 */
function ClassCard({ classId, label }: { classId: string; label: string }) {
  const { t } = useTranslation();
  const pet = useClassPet(classId);
  const data = pet.data;
  const state = data ? petStage(data.points, data.students) : undefined;
  const progress = state ? Math.min(1, Math.max(0, state.progress)) : 0;
  return (
    <LimeCard
      label={t('home.homeroom', { class: label })}
      action={<Ring to="/journal" label={t('home.openJournal')} />}
      art={
        <TaskArt
          kind="catchUp"
          tone="lime"
          size={150}
          ghost
          className="pointer-events-none absolute -right-4 -bottom-3 -z-10"
        />
      }
    >
      {data && state ? (
        <>
          <p
            className="mt-1 truncate font-display text-[36px] leading-[44px] font-light tracking-[-0.035em]"
            style={{ color: INK }}
          >
            {t('pet.name', { class: label })}
          </p>
          <p className="text-[13.5px]">
            {t('pet.title')} · {t(`pet.stages.${state.stage.key}`)}
          </p>
          <span className="relative mt-4 block h-[3px] rounded-full bg-white/70">
            <span
              className="absolute inset-y-0 left-0 rounded-full"
              style={{ width: `${progress * 100}%`, backgroundColor: DARK }}
            />
            <span
              className="absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white"
              style={{ left: `${progress * 100}%`, boxShadow: `inset 0 0 0 3px ${DARK}` }}
            />
          </span>
          <p className="mt-2 text-[12.5px]">
            {state.next
              ? t('pet.next', {
                  stage: t(`pet.stages.${state.next.key}`),
                  percent: Math.round(progress * 100),
                })
              : t('pet.max')}
          </p>
          <span className="mt-4 flex flex-wrap gap-1.5">
            <Chip soft>
              {t('pet.week')}: {t('pet.weekPoints', { count: data.weekPoints })}
            </Chip>
            {data.weekHomework !== null && (
              <Chip soft>
                {t('pet.homework', { percent: Math.round(data.weekHomework * 100) })}
              </Chip>
            )}
            {data.weekAttendance !== null && (
              <Chip soft>
                {t('pet.attendance', { percent: Math.round(data.weekAttendance * 100) })}
              </Chip>
            )}
            {data.weekTests > 0 && <Chip soft>{t('pet.tests', { count: data.weekTests })}</Chip>}
          </span>
          <p className="mt-3 max-w-[300px] text-[12px] leading-[17px]" style={{ color: INK_SOFT }}>
            {t('pet.how')}
          </p>
        </>
      ) : (
        <p
          className="mt-1 font-display text-[36px] leading-[44px] font-light tracking-[-0.035em]"
          style={{ color: INK }}
        >
          {t('home.openJournal')}
        </p>
      )}
    </LimeCard>
  );
}

function TeacherHome({ ctx }: { ctx: Extract<ViewContext, { kind: 'teacher' }> }) {
  const { t } = useTranslation();
  const name = useFirstName(ctx.memberId, t('roles.teacher'));
  const day = useUpcomingDay(ctx.schoolId, { kind: 'teacher', memberId: ctx.memberId });
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <Hero
        left={
          <Greeting name={name}>
            <DayLine day={day} />
            <div className="flex flex-wrap gap-2">
              <DemoLessonButton />
              <DemoClassLessonButton />
              {!ctx.homeroomClass && (
                <Link to="/journal" className={pillClass('dark', 'sm')}>
                  {t('home.openJournal')}
                  <PillIcon icon={ArrowUpRightIcon} size="sm" />
                </Link>
              )}
            </div>
          </Greeting>
        }
        right={
          ctx.homeroomClass && (
            <ClassCard classId={ctx.homeroomClass.id} label={ctx.homeroomClass.label} />
          )
        }
      />
      <div className="flex min-w-0 flex-col gap-4 xl:col-span-12 xl:flex-row xl:items-start">
        <div className="min-w-0 xl:flex-[5]">
          <Lessons day={day} perspective="teacher" memberId={ctx.memberId} />
        </div>
        <div className="min-w-0 xl:flex-[7] empty:hidden">
          <SchoolLife schoolId={ctx.schoolId} />
        </div>
      </div>
    </div>
  );
}

// —— the office ——

function AdminHome({ ctx }: { ctx: Extract<ViewContext, { kind: 'admin' }> }) {
  const { t } = useTranslation();
  const name = useFirstName(ctx.memberId, t('roles.admin'));
  const day = useUpcomingDay(ctx.schoolId, { kind: 'school' });
  const date = day.date ?? todayIso();
  const absences = useAbsencesOn(ctx.schoolId, date);
  const now = useMinute();
  const all = day.lessons;
  const changes = all.filter(
    (l) => l.status === 'cancelled' || l.substitute || l.format !== 'in_person',
  );
  const counts = [
    { label: t('home.online'), value: all.filter((l) => l.format === 'online').length },
    { label: t('home.shelter'), value: all.filter((l) => l.format === 'shelter').length },
    { label: t('home.substitutions'), value: all.filter((l) => l.substitute).length },
    { label: t('home.cancelled'), value: all.filter((l) => l.status === 'cancelled').length },
    {
      label: t('home.absentToday'),
      value: new Set((absences.data ?? []).map((a) => a.student_member_id)).size,
    },
  ];
  const pending = day.isPending;
  const when = whenOf(t, day);
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <Hero
        left={
          <Greeting name={name}>
            {pending ? (
              <p className="h-9" aria-busy />
            ) : (
              <p
                className="max-w-[620px] font-display text-[21px] leading-[38px] font-light tracking-[-0.01em] md:text-[24px] md:leading-[42px]"
                style={{ color: INK_SOFT }}
              >
                <span style={{ color: INK }}>{when}</span> —{' '}
                <Mark tone="lavender">{t('student.lessonsCount', { count: all.length })}</Mark>
                {changes.length > 0 ? (
                  <>
                    ,{' '}
                    <Mark tone="apricot">{t('home.changesCount', { count: changes.length })}</Mark>.
                  </>
                ) : (
                  <>. {t('home.noChanges')}</>
                )}
              </p>
            )}
          </Greeting>
        }
        right={
          <LimeCard
            label={
              day.isToday || !day.date
                ? t('home.schoolToday')
                : t('home.schoolOn', { day: shortDate(day.date) })
            }
            action={<Ring to="/schedule" label={t('home.fullSchedule')} />}
          >
            <div className="mt-2">
              <Count big value={pending ? '—' : all.length} label={t('home.lessonsToday')} />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-x-3 gap-y-3 rounded-[20px] bg-white/80 p-3.5">
              {counts.map((c) => (
                <Count key={c.label} value={pending ? '—' : c.value} label={c.label} />
              ))}
            </div>
          </LimeCard>
        }
      />
      {/* what every class is doing now — the director's one screen — right under the greeting */}
      {!pending && (
        <div className="min-w-0 empty:hidden xl:col-span-12">
          <ClassesNow lessons={all} date={date} isToday={day.isToday} />
        </div>
      )}
      <div className="flex min-w-0 flex-col gap-4 xl:col-span-12 xl:flex-row xl:items-start">
        <div className="min-w-0 xl:flex-[5]">
          <Block>
            <Head title={t('home.changes')} count={changes.length}>
              <OpenArrow to="/schedule" label={t('home.fullSchedule')} soft />
            </Head>
            {pending ? (
              <div
                className="h-[120px] animate-pulse rounded-[20px]"
                style={{ backgroundColor: PAPER }}
                aria-busy
              />
            ) : changes.length === 0 ? (
              <Quiet>{t('home.noChanges')}</Quiet>
            ) : (
              <div className="flex flex-col gap-2">
                {changes.map((l) => (
                  <LessonRow key={l.id} lesson={l} perspective="school" family={false} now={now} />
                ))}
              </div>
            )}
          </Block>
        </div>
        <div className="min-w-0 empty:hidden xl:flex-[7]">
          <SchoolLife schoolId={ctx.schoolId} />
        </div>
      </div>
    </div>
  );
}

// —— the psychologist ——

/** A thread's kind as a filled icon in its colour: urgent, bullying, a talk. */
function ThreadDot({ thread }: { thread: CareThread }) {
  if (thread.urgent) return <Dot icon={SirenIcon} tone="coral" size={36} />;
  if (thread.kind === 'bullying') return <Dot icon={ShieldIcon} tone="lavender" size={36} />;
  return <Dot icon={ChatCircleIcon} tone="sky" size={36} />;
}

/** One class: a bar of the share of hard days, the faces of the week, the change since last week. */
function MoodRow({ row, previous }: { row: ClassMood; previous?: ClassMood }) {
  const { t } = useTranslation();
  const percent = Math.round(row.hardShare * 100);
  const before = previous ? Math.round(previous.hardShare * 100) : undefined;
  return (
    <li
      className="grid grid-cols-[52px_minmax(0,1fr)_auto] items-center gap-3 py-3"
      style={{ borderTop: `1px solid ${LINE}` }}
    >
      <span
        className="font-display text-[22px] leading-7 font-light tracking-[-0.02em]"
        style={{ color: INK }}
      >
        {row.classLabel}
      </span>
      <span className="min-w-0">
        <span className="block h-2 overflow-hidden rounded-full" style={{ backgroundColor: PAPER }}>
          <span
            className="block h-full rounded-full transition-[width] duration-700"
            style={{ width: `${Math.max(percent, 2)}%`, backgroundImage: grad('coral') }}
          />
        </span>
        <span
          className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px]"
          style={{ color: INK_SOFT }}
        >
          <span>{t('care.staff.respondents', { count: row.respondents })}</span>
          {MOODS.map((m) => (
            <span key={m} className="inline-flex items-center gap-1" title={t(`care.moods.${m}`)}>
              <MoodArt mood={m} size={16} />
              {row.counts[m]}
            </span>
          ))}
        </span>
      </span>
      <span className="text-right">
        <span
          className="block font-display text-[22px] leading-7 font-light tracking-[-0.02em]"
          style={{ color: INK }}
        >
          {percent}%
        </span>
        {before !== undefined && before !== percent && (
          <span className="block text-[11.5px]" style={{ color: INK_SOFT }}>
            {percent > before ? '▲' : '▼'} {t('care.staff.lastWeekWas', { percent: before })}
          </span>
        )}
      </span>
    </li>
  );
}

function PsychologistHome({ ctx }: { ctx: Extract<ViewContext, { kind: 'psychologist' }> }) {
  const { t } = useTranslation();
  const name = useFirstName(ctx.memberId, t('roles.psychologist'));
  const whoLabel = useWhoLabel();
  const threads = useCareThreads(ctx.schoolId);
  const today = todayIso();
  const week = useMoodOverview(ctx.schoolId, addDays(today, -6), today);
  const last = useMoodOverview(ctx.schoolId, addDays(today, -13), addDays(today, -7));
  const all = threads.data ?? [];
  const unread = all.filter((th) => th.unreadForStaff);
  const urgent = all.filter((th) => th.urgent && th.status === 'open').length;
  const bullying = all.filter((th) => th.kind === 'bullying' && th.status === 'open').length;
  const rows = [...(week.data ?? [])].sort((a, b) => b.hardShare - a.hardShare);
  const value = (n: number) => (threads.isPending ? '—' : n);
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <Hero
        left={
          <Greeting name={name}>
            {threads.isPending ? (
              <p className="h-9" aria-busy />
            ) : (
              <p
                className="max-w-[620px] font-display text-[21px] leading-[38px] font-light tracking-[-0.01em] md:text-[24px] md:leading-[42px]"
                style={{ color: INK_SOFT }}
              >
                <span style={{ color: INK }}>{t('care.staff.homeTitle')}</span>
                {unread.length === 0 && urgent === 0 ? (
                  <>. {t('care.staff.empty')}</>
                ) : (
                  <>
                    {' — '}
                    {unread.length > 0 && (
                      <Mark tone="sky">{t('care.staff.newCount', { count: unread.length })}</Mark>
                    )}
                    {unread.length > 0 && urgent > 0 && ', '}
                    {urgent > 0 && (
                      <Mark tone="coral">{t('care.staff.urgentCount', { count: urgent })}</Mark>
                    )}
                    .
                  </>
                )}
              </p>
            )}
          </Greeting>
        }
        right={
          <LimeCard label={t('care.staff.inbox')}>
            <div className="mt-3 grid grid-cols-3 gap-3">
              <Count big value={value(unread.length)} label={t('care.staff.filters.unread')} />
              <Count big value={value(urgent)} label={t('care.staff.filters.urgent')} />
              <Count big value={value(bullying)} label={t('care.staff.filters.bullying')} />
            </div>
            <Link
              to="/care"
              className="group mt-5 flex h-12 items-center gap-3 rounded-[20px] bg-white pr-2 pl-4 text-[14px] font-medium"
              style={{ color: INK }}
            >
              <span className="min-w-0 flex-1 truncate">{t('care.staff.open')}</span>
              <span
                className={`${ringSmall} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}
              >
                <ArrowUpRightIcon size={14} weight="bold" aria-hidden />
              </span>
            </Link>
          </LimeCard>
        }
      />
      <div className="flex min-w-0 flex-col gap-4 xl:col-span-12 xl:flex-row xl:items-start">
        <div className="min-w-0 xl:flex-[6]">
          <Block>
            <Head title={t('care.staff.inbox')} count={unread.length}>
              <Ring to="/care" label={t('care.staff.open')} />
            </Head>
            {threads.isPending ? (
              <div
                className="h-[160px] animate-pulse rounded-[20px]"
                style={{ backgroundColor: PAPER }}
                aria-busy
              />
            ) : unread.length === 0 ? (
              <Quiet>{t('care.staff.empty')}</Quiet>
            ) : (
              <ul className="flex flex-col">
                {unread.slice(0, 6).map((th) => (
                  <li key={th.id} style={{ borderTop: `1px solid ${LINE}` }}>
                    <Link
                      to="/care"
                      search={{ thread: th.id }}
                      className="group flex items-center gap-3.5 py-3"
                    >
                      <ThreadDot thread={th} />
                      <span className="min-w-0 flex-1">
                        <span
                          className="block truncate text-[16px] leading-[22px] font-medium"
                          style={{ color: INK }}
                        >
                          {threadTitle(t, th)}
                        </span>
                        <span className="block truncate text-[12.5px]" style={{ color: INK_SOFT }}>
                          {whoLabel(th)}
                        </span>
                      </span>
                      {th.urgent && <Label tone="coral">{t('care.staff.filters.urgent')}</Label>}
                      <span
                        className={`${ringSmall} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}
                      >
                        <ArrowUpRightIcon size={14} weight="bold" aria-hidden />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Block>
        </div>
        <div className="min-w-0 xl:flex-[6]">
          <Block>
            <Head title={t('care.staff.mood')} note={t('care.staff.week')} />
            <p className="-mt-2 mb-3 text-[13.5px]" style={{ color: INK_SOFT }}>
              {t('care.staff.moodHint')}
            </p>
            <p
              className="mb-1 flex items-center gap-1.5 text-[12.5px] font-medium"
              style={{ color: INK_SOFT }}
            >
              {capitalize(t('care.staff.hard'))}
              <MoodArt mood="sad" size={18} />
              <MoodArt mood="angry" size={18} />
              <MoodArt mood="anxious" size={18} />
            </p>
            {week.isPending ? (
              <div
                className="h-[120px] animate-pulse rounded-[20px]"
                style={{ backgroundColor: PAPER }}
                aria-busy
              />
            ) : rows.length === 0 ? (
              <Quiet>{t('care.staff.noMood')}</Quiet>
            ) : (
              <ul className="flex flex-col">
                {rows.map((row) => (
                  <MoodRow
                    key={row.classId}
                    row={row}
                    previous={(last.data ?? []).find((p) => p.classId === row.classId)}
                  />
                ))}
              </ul>
            )}
          </Block>
        </div>
      </div>
    </div>
  );
}

function RoleHome() {
  const ctx = useCurrentContext();
  switch (ctx.kind) {
    case 'student':
      return <StudentHome key={ctx.key} ctx={ctx} />;
    case 'guardian':
      return <FamilyHome key={ctx.key} ctx={ctx} />;
    case 'teacher':
      return <TeacherHome key={ctx.key} ctx={ctx} />;
    case 'admin':
      return <AdminHome key={ctx.key} ctx={ctx} />;
    case 'psychologist':
      return <PsychologistHome key={ctx.key} ctx={ctx} />;
  }
}

export function HomePage() {
  return <RoleHome />;
}
